import { useContext, useEffect, useState } from 'react';
import { chargesBreakdownStore, discountsBreakdownStore } from '@/store/priceBreakdown';
import {
  PRODUCT_PRICE,
  SHIPPING_COST,
  setDiscounts,
  PAYMENT_METHOD,
  HANDLING_COST,
  PACKAGING_GUARANTEE,
} from '@/utils/products/products';
import { Discount, CartIcon } from '@desquared/wave-components-library';
import User from '@/store/user';
import { convertFromCents, formatMoney } from '@/utils/amountUtils';
import { getPaymentConfigurations } from '@/utils/getConfiguration';
import { useTranslations } from 'next-intl';
import { PaymentOptionStore } from '@/store/sideSheet';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { useConfiguration } from '@/contexts/ConfigContext.jsx';

const useChargeDiscountBreakdown = (
  order,
  bags,
  discountstate,
  hasCoupon,
  coupon,
  cartPrice,
  setCartPrice,
  setBreakdownCategories,
  setPaymentOptions,
  amountToRedeem,
  pointsToRedeem,
  couponDiscount
) => {
  function isCreditBalanceDisplayableAndValid(paymentOptions) {
    const entry = paymentOptions?.find(item => item.type === 'CREDIT_BALANCE');
    return entry && entry?.displayable && entry?.isValid ? entry?.amount : 0;
  }
  const creditBalance =
    User.getValue()?.creditBalance?.value || isCreditBalanceDisplayableAndValid(order?.paymentOptions);
  const paymentConfigOptions = getPaymentConfigurations();
  const { formatter } = useContext(CurrencyContext);
  const { config } = useConfiguration();

  const checkPaymentAvailabilityOption = option => {
    if (paymentConfigOptions) {
      const checkOption = paymentConfigOptions.find(el => el.type === option);
      return checkOption.enabled;
    } else {
      return false;
    }
  };
  const checkPaymentAvailabilityOptionVisibility = option => {
    if (paymentConfigOptions) {
      const checkOption = paymentConfigOptions.find(el => el.type === option);
      return checkOption.visibleToUser;
    } else {
      return false;
    }
  };

  const t = useTranslations('checkout');
  const [paymentType, setPaymentType] = useState(PaymentOptionStore.getValue()?.type);
  const [totalPrice, setTotalPrice] = useState(0);
  useEffect(() => {
    const paymentType$ = PaymentOptionStore.subscribe(setPaymentType);

    return () => {
      paymentType$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let productPriceDiscount = setDiscounts(order?.discounts, 'PRODUCTS');
    let discountPriceCampaign = setDiscounts(order?.discounts, 'WHOLE_CART');
    let charges = [
      {
        description: t('product_value'),
        value: formatter.format(order?.orderItemsPriceWithoutDiscounts / 100),
        dataRowType: 'DataRowInformation',
      },
      {
        description:
          order?.fees?.filter(el => el.type === SHIPPING_COST)?.length > 0
            ? order?.fees?.filter(el => el.type === SHIPPING_COST)[0]?.title
            : t('shipping_cost'),
        value:
          order?.fees?.filter(el => el.type === SHIPPING_COST)?.length > 0
            ? formatter.format(order?.fees?.filter(el => el.type === SHIPPING_COST)[0]?.amount / 100)
            : formatter.format(0),
        dataRowType: 'DataRowInformation',
      },
    ];

    // Conditionally add the payment method fee

    if (
      order?.fees?.filter(el => el.type === PAYMENT_METHOD)?.length > 0 &&
      (paymentType?.type === 'cash' || paymentType === 'cash')
    ) {
      charges.push({
        description: order?.fees?.filter(el => el.type === PAYMENT_METHOD)[0]?.title,
        value: formatter.format(order?.fees?.filter(el => el.type === PAYMENT_METHOD)[0]?.amount / 100),
        dataRowType: 'DataRowInformation',
      });
    }
    if (order?.fees?.filter(el => el.type === HANDLING_COST)?.length > 0) {
      charges.push({
        description: order?.fees?.filter(el => el.type === HANDLING_COST)[0]?.title,
        value: formatter.format(order?.fees?.filter(el => el.type === HANDLING_COST)[0]?.amount / 100),
        dataRowType: 'DataRowInformation',
      });
    }
    if (
      config?.catalog?.product?.packagingFee?.enabled &&
      order?.fees?.filter(el => el.type === PACKAGING_GUARANTEE)?.length > 0
    ) {
      charges.push({
        description: order?.fees?.filter(el => el.type === PACKAGING_GUARANTEE)[0]?.title,
        value: formatter.format(order?.fees?.filter(el => el.type === PACKAGING_GUARANTEE)[0]?.amount / 100),
        dataRowType: 'DataRowInformation',
      });
    }

    if (bags) {
      charges.push({
        description: `${t('shopping_bags')} (${order?.plasticBagsInfo.amount})`,
        value: formatter.format(order?.plasticBagsInfo.price / 100),
        dataRowType: 'DataRowInformation',
      });
    }

    const updatedCharges = charges.map(charge => {
      if (charge.dataRowType === 'DataRowToggleOption') {
        return {
          ...charge,
          stateSelected: bags,
        };
      }
      return charge;
    });
    chargesBreakdownStore.update(updatedCharges);

    let discounts = [];
    if (productPriceDiscount > 0 && productPriceDiscount)
      discounts.push({
        description: t('discounts'),
        value: productPriceDiscount ? '- ' + formatter.format(productPriceDiscount / 100) : formatter.format(0),
        dataRowType: 'DataRowInformation',
      });

    const discountCampaign = {
      description: t('campaigns'),
      value: discountPriceCampaign ? '- ' + formatter.format(discountPriceCampaign / 100) : formatter.format(0),
      dataRowType: 'DataRowInformation',
    };
    if (discountPriceCampaign && discountPriceCampaign > 0) {
      discounts.push(discountCampaign);
    }

    if (couponDiscount.isValid && checkPaymentAvailabilityOption('COUPON')) {
      discounts.push({
        description: couponDiscount.message,
        value: '- ' + formatter.format(couponDiscount.amount / 100),
        dataRowType: 'DataRowInformation',
      });
    }
    if (pointsToRedeem > 0) {
      setPaymentOptions(prev => [
        ...prev.filter(val => val.type !== 'POINTS_REDEMPTION'),
        { type: 'POINTS_REDEMPTION', value: pointsToRedeem },
      ]);
      discounts.splice(1, 0, {
        description: t('points_redemption'),
        value: '- ' + formatter.format(convertFromCents(amountToRedeem)),
        dataRowType: 'DataRowInformation',
      });
    } else {
      setPaymentOptions(prev => [...prev.filter(val => val.type !== 'POINTS_REDEMPTION')]);
    }

    if (creditBalance !== undefined && creditBalance > 0 && checkPaymentAvailabilityOption('CREDIT_BALANCE')) {
      if (checkPaymentAvailabilityOptionVisibility('CREDIT_BALANCE')) {
        setPaymentOptions(prev => [
          ...prev.filter(val => val.type !== 'CREDIT_BALANCE'),
          { type: 'CREDIT_BALANCE', value: creditBalance },
        ]);
      }
      discounts.splice(1, 0, {
        description: t('credit_balance'),
        value: '- ' + formatter.format(creditBalance / 100),
        dataRowType: 'DataRowInformation',
      });
    }

    const updatedDiscounts = discounts.map(discount => {
      if (discount.dataRowType === 'DataRowToggleOption') {
        return {
          ...discount,
          stateSelected: discountstate,
        };
      }
      if (discount.dataRowType === 'DataRowInputField') {
        return {
          ...discount,
          stateSelected: hasCoupon,
          value: coupon,
          helperText: coupon,
        };
      }
      return discount;
    });

    discountsBreakdownStore.update(updatedDiscounts);

    let disc = 0;
    if (productPriceDiscount) {
      disc = order?.discounts?.reduce((sum, item) => {
        if (item.shouldBeAppliedToCartItemsPrice) {
          return sum + item.amount;
        }
        return sum;
      }, 0);
    }

    let bagPrice = 0;
    if (bags) bagPrice = order?.plasticBagsInfo.price;
    if (
      order?.discounts?.length > 0 ||
      couponDiscount.isValid ||
      pointsToRedeem > 0 ||
      (creditBalance > 0 && checkPaymentAvailabilityOption('CREDIT_BALANCE'))
    ) {
      setBreakdownCategories([
        {
          headerTitle: t('charges_title'),
          headerIcon: CartIcon,
          items: chargesBreakdownStore.getValue(),
        },
        {
          headerTitle: t('discounts_title'),
          headerIcon: Discount,
          items: discountsBreakdownStore.getValue(),
        },
      ]);
    } else {
      setBreakdownCategories([
        {
          headerTitle: t('charges_title'),
          headerIcon: CartIcon,
          items: chargesBreakdownStore.getValue(),
        },
      ]);
    }

    let newCartPrice = order?.totalPrice - couponDiscount.amount;

    // if (order?.fees && order.fees.find(f => f.type === 'SHIPPING_COST')) {
    //   newCartPrice += order.fees.find(f => f.type === 'SHIPPING_COST').amount;
    // }

    if (amountToRedeem && amountToRedeem > 0) {
      newCartPrice -= amountToRedeem;
    }

    setCartPrice(formatter.format(convertFromCents(order?.totalPrice)));
  }, [bags, discountstate, hasCoupon, coupon, amountToRedeem, couponDiscount, paymentType, cartPrice]);

  return { bags, discountstate, hasCoupon, coupon, cartPrice };
};

export default useChargeDiscountBreakdown;
